import {
  Controller,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { Field, FieldContent, FieldError, FieldLabel, CreatableCombobox } from '@bliks-io/ui';

/**
 * Deduplicate, drop empties, and sort — the canonical way to turn entity
 * field values into CreatableCombobox suggestions.
 */
export function distinctValues(values: (string | undefined)[] | undefined): string[] {
  return [...new Set(values?.filter((value): value is string => !!value))].sort((a, b) =>
    a.localeCompare(b)
  );
}

export interface FormCreatableComboboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Field label
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Suggested values (already deduplicated, e.g. via distinctValues)
   */
  options: string[];

  /**
   * Whether the options are still loading — disables the combobox so it
   * cannot offer "Create" for a value that already exists
   */
  isLoading?: boolean;
}

/**
 * React Hook Form adapter for CreatableCombobox: suggests the given options
 * while still allowing a brand-new value via "Create". Entity-specific
 * wrappers (system category, artifact type) derive the options and delegate
 * here.
 */
export function FormCreatableCombobox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  placeholder,
  options,
  isLoading = false
}: FormCreatableComboboxProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const fieldErrorMessage = useFieldErrorMessage();

  // Display from the raw watched value, not Controller's field.value, which
  // masks a cleared (undefined) value back to the field's original value. See
  // FormUserSelect for the full explanation.
  const value = useWatch({ control, name });

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error}>
          {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
          <FieldContent>
            <CreatableCombobox
              id={name}
              options={options}
              value={value as string | undefined}
              onValueChange={field.onChange}
              placeholder={isLoading ? t('common.loading') : placeholder}
              allowClear
              disabled={isLoading}
            />
            {fieldState.error?.message && (
              <FieldError>{fieldErrorMessage(fieldState.error)}</FieldError>
            )}
          </FieldContent>
        </Field>
      )}
    />
  );
}

FormCreatableCombobox.displayName = 'FormCreatableCombobox';
